import { Injectable } from '@angular/core';

import { AsmModeService } from 'src/app/spartacus/features/asm/asm-mode.service';

import { AsmAuthStorageService } from '@spartacus/asm/root';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root',
})
export class LoginAuthorizeService {
  private readonly STANDARD_LOGIN_TRIGGERED_KEY = 'standardLoginTriggered';
  private readonly ASM_LOGIN_TRIGGERED_KEY = 'asmLoginTriggered';
  private readonly DEALER_LOGIN_IN_PROGRESS_KEY = 'dealerLoginInProgress';
  private readonly LAST_AUTHORIZE_CONTEXT_KEY = 'lastAuthorizeContext';

  /*
  private storageService = inject(StorageService);
  private asmModeService = inject(AsmModeService);
  private asmAuthStorageService = inject(AsmAuthStorageService);
  private auth = inject(AuthService);
  private winRef = inject(WindowRef);
  */

  constructor(
    private storageService: StorageService,
    private asmModeService: AsmModeService,
    private asmAuthStorageService: AsmAuthStorageService
  ) {}

  private get asmEnabled(): boolean {
    return this.asmModeService.isAsmMode();
  }

  /**
   * Public entry point for triggering ASM or standard authorization.
   */

  reAuthorize(initLoginFlow: () => void): boolean {
    this.resetLoginTriggersIfContextChanged();

    const shouldAuthorize = this.asmEnabled ? this.onAsmSignIn() : this.onStandardSignIn();

    if (!shouldAuthorize) {
      return false;
    }

    try {
      initLoginFlow();
      return true;
    } catch (error) {
      console.error('[Login] Failed to initiate login flow.', error);

      return false;
    }
  }

  private onStandardSignIn(): boolean {
    /*
     * Check whether the application is switching from ASM
     * to standard login.
     */
    if (this.asmModeService.checkSwitchToNonASM()) {
      this.asmModeService.disableAsmMode();
      this.storageService.removeSessionStorageItem(this.ASM_LOGIN_TRIGGERED_KEY);
      this.storageService.setSessionStorageItem(this.STANDARD_LOGIN_TRIGGERED_KEY, 'true');
    }

    const standardLoginTriggered = this.storageService.getSessionStorageItem(this.STANDARD_LOGIN_TRIGGERED_KEY);

    if (standardLoginTriggered === 'true') {
      return false;
    }

    // Ensure ASM mode is disabled.
    this.asmModeService.disableAsmMode();

    // Clear the previous ASM trigger.
    this.storageService.removeSessionStorageItem(this.ASM_LOGIN_TRIGGERED_KEY);

    // Prevent another standard authorization request.
    this.storageService.setSessionStorageItem(this.STANDARD_LOGIN_TRIGGERED_KEY, 'true');

    try {
      return true;
    } catch (error) {
      /*
       * Reset the flag if initLoginFlow throws synchronously,
       * allowing another attempt.
       */
      //this.storageService.removeSessionStorageItem(this.STANDARD_LOGIN_TRIGGERED_KEY);

      console.error('[LoginAuthorizeService] Standard login failed.', error);

      throw error;
    }
  }

  private onAsmSignIn(): boolean {
    const asmLoginTriggered = this.storageService.getSessionStorageItem(this.ASM_LOGIN_TRIGGERED_KEY);

    if (asmLoginTriggered === 'true') {
      return false;
    }

    this.asmModeService.enableAsmMode();

    // Clear the previous standard login trigger.
    this.storageService.removeSessionStorageItem(this.STANDARD_LOGIN_TRIGGERED_KEY);

    // Prevent another ASM authorization request.
    this.storageService.setSessionStorageItem(this.ASM_LOGIN_TRIGGERED_KEY, 'true');

    // Store the token under the CS Agent target.
    this.asmAuthStorageService.switchTokenTargetToCSAgent();

    try {
      return true;
    } catch (error) {
      /*
       * Reset the flag if initLoginFlow throws synchronously,
       * allowing another attempt.
       */
      //this.storageService.removeSessionStorageItem(this.ASM_LOGIN_TRIGGERED_KEY);

      console.error('[LoginAuthorizeService] ASM login failed.', error);

      throw error;
    }
  }

  private resetLoginTriggersIfContextChanged(): void {
    const businessType = this.storageService.getSessionStorageItem('ShimanoBusinessType')?.toLowerCase();

    const country = this.storageService.getSessionStorageItem('ShimanoCountry')?.toLowerCase();

    const language = this.storageService.getSessionStorageItem('ShimanoLanguage')?.toLowerCase();

    if (!businessType || !country || !language) {
      return;
    }

    const currentContext = `${businessType}|${country}|${language}`;

    const lastAuthorizeContext = this.storageService.getSessionStorageItem(this.LAST_AUTHORIZE_CONTEXT_KEY);

    if (currentContext === lastAuthorizeContext) {
      return;
    }

    /*
     * Save the new context before calling initLoginFlow().
     */
    this.storageService.setSessionStorageItem(this.LAST_AUTHORIZE_CONTEXT_KEY, currentContext);

    if (this.asmEnabled) {
      this.storageService.removeSessionStorageItem(this.ASM_LOGIN_TRIGGERED_KEY);

      this.storageService.removeSessionStorageItem(this.DEALER_LOGIN_IN_PROGRESS_KEY);

      return;
    }

    this.storageService.removeSessionStorageItem(this.STANDARD_LOGIN_TRIGGERED_KEY);
  }

  /**
   * Can be called during logout or when all login-related
   * session flags must be cleared.
   */
  reset(): void {
    this.storageService.removeSessionStorageItem(this.STANDARD_LOGIN_TRIGGERED_KEY);

    this.storageService.removeSessionStorageItem(this.ASM_LOGIN_TRIGGERED_KEY);

    this.storageService.removeSessionStorageItem(this.DEALER_LOGIN_IN_PROGRESS_KEY);

    this.storageService.removeSessionStorageItem(this.LAST_AUTHORIZE_CONTEXT_KEY);
  }
}
